import { motion } from 'framer-motion'
import { useEffect, useRef, useState, type PropsWithChildren } from 'react'
import { cn } from '~/shared/lib/cn'
import { NateraIcon, Text } from '~/shared/ui'
import type { DataExtractionMode } from '../types'

export function FieldsSubgroup({
  children,
  title,
  titleIcon,
  description,
  mode,
}: PropsWithChildren<{
  title?: string
  titleIcon?: string
  description?: string
  mode: DataExtractionMode
}>) {
  const observerHackRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  // This is a hack to make subgroup render when it is visible in the scroll container to avoid post-animation layout issues
  useEffect(() => {
    const scrollContainer = document.querySelector('.js-review-form-scroll-container')
    if (!scrollContainer || !observerHackRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1]
        if (entry) {
          setIsVisible(entry.isIntersecting)
        }
      },
      {
        root: scrollContainer,
        rootMargin: '0px',
        threshold: 0,
      },
    )

    observer.observe(observerHackRef.current)

    return () => {
      observer.disconnect()
    }
  }, [mode])

  return (
    <motion.div layout>
      <div
        ref={observerHackRef}
        data-visible={isVisible}
      />
      {!!title && (
        <motion.div
          layout
          className="mt-4 flex items-center gap-3 py-3"
        >
          {titleIcon === 'natera' && (
            <span className="bg-surface-medium flex size-6 items-center justify-center rounded-full">
              <NateraIcon size="18" />
            </span>
          )}
          <Text appearance="SUBTITLE2">{title}</Text>
        </motion.div>
      )}
      {!!description && (
        <motion.div layout>
          <Text
            className="mt-1 mb-5 block"
            appearance="CAPTION"
          >
            {description}
          </Text>
        </motion.div>
      )}
      <motion.div
        layout
        className={cn('mb-1 grid grid-cols-2 gap-4', {
          'grid-cols-1': mode === 'edit',
        })}
      >
        {children}
      </motion.div>
    </motion.div>
  )
}
